import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useConfigStore, useUserStore, useAmlCheckStore } from "@/store";
import { sendMessageToParent } from "@/utils/messaging";
import {
  WIDGET_HEIGHT,
  WIDGET_WIDTH,
  PAGES,
  MODE_FLOAT,
  MODE_INLINE,
  ALLOWED_WIDGET_MODES,
  THEME_LIGHT,
} from "@/config";

export const useWidgetListener = () => {
  const navigate = useNavigate();
  const setIsOpen = useConfigStore((state) => state.setIsOpen);
  const setExternalAmlData = useAmlCheckStore(
    (state) => state.setExternalAmlData,
  );
  const setInitParams = useConfigStore((state) => state.setInitParams);
  const setExternalId = useConfigStore((state) => state.setExternalId);
  const setUsername = useConfigStore((state) => state.setUsername);
  const setWidgetConfig = useConfigStore((state) => state.setWidgetConfig);
  const fetchUserData = useUserStore((state) => state.fetchUserData);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const originFromUrl = queryParams.get("parentOrigin");
    const appIdFromUrl = queryParams.get("appId");
    const widgetModeFromUrl = queryParams.get("mode") || MODE_FLOAT;

    setInitParams({
      parentOrigin: originFromUrl,
      appId: appIdFromUrl,
    });

    setWidgetConfig({
      widgetMode: ALLOWED_WIDGET_MODES.includes(widgetModeFromUrl)
        ? (widgetModeFromUrl as typeof MODE_FLOAT | typeof MODE_INLINE)
        : MODE_FLOAT,
      theme: THEME_LIGHT,
    });

    const openWidgetWindow = () => {
      setIsOpen(true);
      const width =
        widgetModeFromUrl === MODE_INLINE ? "100%" : `${WIDGET_WIDTH}px`;
      const height =
        widgetModeFromUrl === MODE_INLINE ? "100%" : `${WIDGET_HEIGHT}px`;

      sendMessageToParent(
        "RESIZE",
        {
          width,
          height,
          isOpen: true,
        },
        originFromUrl,
      );
    };

    if (widgetModeFromUrl === MODE_INLINE) {
      openWidgetWindow();
    }

    const handleMessage = (event: MessageEvent) => {
      if (originFromUrl && event.origin !== originFromUrl) {
        return;
      }

      const data = event.data;

      if (typeof data !== "object" || data.source !== "amlbot-widget") {
        return;
      }

      switch (data.type) {
        case "INIT":
          setExternalId(data.payload.externalId);
          setUsername(data.payload.username);

          fetchUserData();
          break;
        case "OPEN":
          openWidgetWindow();
          break;
        case "SEND_DATA":
          setExternalAmlData({ ...data.payload });
          navigate(PAGES.HOME);
          openWidgetWindow();
          break;

        case "CLOSE":
          if (widgetModeFromUrl === MODE_INLINE) break;
          setIsOpen(false);
          sendMessageToParent("CLOSE_WIDGET", {}, originFromUrl);
          break;

        default:
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    sendMessageToParent("WIDGET_READY", {}, originFromUrl || "*");

    return () => window.removeEventListener("message", handleMessage);
  }, [
    navigate,
    setIsOpen,
    setExternalAmlData,
    setInitParams,
    setExternalId,
    setUsername,
    setWidgetConfig,
    fetchUserData,
  ]);
};
